import React, { useState, useMemo } from 'react';
import { Modal, Form, Input, Select, Button, message, TreeSelect } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { CREATE_USER, CreateUserMutationVariables, CreateUserMutationResponse } from './Users';
import {
  SpaceRelation,
  ASSIGN_USER_TO_SPACE,
  AssignUserToSpaceVariables,
  AssignUserToSpaceResponse,
  AUTH_API_URL,
} from './SpaceAssignments';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';
import {
  flattenHierarchyToSpaceOptions,
  buildTreeDataWithSelectAll,
  getDefaultExpandedKeys,
  getSpaceIdsToAssign,
  SELECT_ALL_VALUE,
} from './SpaceTreeUtils';
import { useAuthUser } from '@/hooks/useAuth';
import { formatOperationError } from '@/api/formatError';

const { Option } = Select;

interface InviteUserModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ open, onCancel, onSuccess }) => {
  const { isSuperUser } = useAuthUser();
  const [form] = Form.useForm();
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [relation, setRelation] = useState<SpaceRelation>('can_view');
  const [spaceError, setSpaceError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('member');

  // Fetch real site hierarchy from main Butlr API
  const { data: hierarchyData } = useQuery(GET_SITE_HIERARCHY, {
    skip: !open,
  });

  const availableSpaces = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return flattenHierarchyToSpaceOptions(sites);
  }, [hierarchyData]);

  // Build tree with "Select All Spaces" root node
  const treeData = useMemo(() => buildTreeDataWithSelectAll(availableSpaces), [availableSpaces]);

  // Expand sites and buildings by default (show up to floor level, rooms collapsed)
  const defaultExpandedKeys = useMemo(
    () => getDefaultExpandedKeys(availableSpaces),
    [availableSpaces],
  );

  const [createUser, { loading: isCreatingUser }] = useMutation<
    CreateUserMutationResponse,
    CreateUserMutationVariables
  >(CREATE_USER, {
    context: { uri: AUTH_API_URL },
  });

  const [assignToSpace, { loading: isAssigning }] = useMutation<
    AssignUserToSpaceResponse,
    AssignUserToSpaceVariables
  >(ASSIGN_USER_TO_SPACE, {
    context: { uri: AUTH_API_URL },
  });

  const isLoading = isCreatingUser || isAssigning;

  const validateSpaceAssignment = (): boolean => {
    const spacesToAssign = getSpaceIdsToAssign(selectedSpaces, availableSpaces);
    if (spacesToAssign.length === 0) {
      setSpaceError('Please assign at least one space');
      return false;
    }
    setSpaceError(null);
    return true;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // Space assignment is optional for admin/owner roles
      const isAdminOrOwner = values.role === 'admin' || values.role === 'owner';
      if (!isAdminOrOwner && !validateSpaceAssignment()) {
        return;
      }

      // Step 1: Create user via auth API
      const roles =
        values.role === 'owner' ? ['owner'] : values.role === 'admin' ? ['admin'] : ['butlr_user'];
      const name = values.email.split('@')[0];
      const result = await createUser({
        variables: {
          input: {
            email: values.email,
            name,
            password: crypto.randomUUID(),
            roles,
          },
        },
      });

      const userId = result.data?.createUser?.id;
      if (!userId) {
        message.error('Failed to create user: no user ID returned');
        return;
      }

      // Step 2: Assign selected spaces to the new user concurrently (skip if none selected)
      const spacesToAssign = getSpaceIdsToAssign(selectedSpaces, availableSpaces);
      if (spacesToAssign.length > 0) {
        const results = await Promise.allSettled(
          spacesToAssign.map((space) =>
            assignToSpace({
              variables: {
                input: {
                  user_id: userId,
                  space_type: space.level,
                  space_id: space.id,
                  relation,
                },
              },
            }),
          ),
        );

        // Resolve each failed assignment to its display name so an admin
        // retrying the invite can see exactly which spaces need a second
        // pass, not just a count. Falls back to the raw id if the name
        // isn't in the hierarchy (should be rare — spacesToAssign comes
        // from availableSpaces itself).
        const nameById = new Map(availableSpaces.map((s) => [s.id, s.name]));
        const failedNames = results
          .map((r, i) =>
            r.status === 'rejected'
              ? nameById.get(spacesToAssign[i].id) ?? spacesToAssign[i].id
              : null,
          )
          .filter((n): n is string => !!n);

        if (failedNames.length > 0) {
          const MAX_NAMES = 5;
          const shown = failedNames.slice(0, MAX_NAMES).join(', ');
          const more =
            failedNames.length > MAX_NAMES ? ` and ${failedNames.length - MAX_NAMES} more` : '';
          message.warning(
            `Member created but ${failedNames.length} of ${spacesToAssign.length} space assignment(s) failed: ${shown}${more}.`,
          );
        } else {
          message.success('Member added successfully!');
        }
      } else {
        message.success('Member added successfully!');
      }
      handleClose();
      onSuccess();
    } catch (error: unknown) {
      console.error('Error adding member:', error);
      message.error(formatOperationError('add member', error));
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedSpaces([]);
    setRelation('can_view');
    setSpaceError(null);
    setSelectedRole('member');
    onCancel();
  };

  const handleRoleChange = (value: string) => {
    setSelectedRole(value);
    if (value === 'admin' || value === 'owner') {
      setSpaceError(null);
    }
  };

  const handleSpaceChange = (values: string[]) => {
    setSelectedSpaces(values);
    const hasRealSpaces = values.some((v) => v !== SELECT_ALL_VALUE);
    if (hasRealSpaces || values.includes(SELECT_ALL_VALUE)) {
      setSpaceError(null);
    }
  };

  // Disable the Invite button until required fields are filled and valid.
  // Email must look like an email; members must have at least one space
  // assigned; admins/owners can submit without a space.
  const emailValue: string | undefined = Form.useWatch('email', form);
  // Loose `non-whitespace@non-whitespace` check: just a UX gate for the
  // submit button. The authoritative validation lives on `type: 'email'`
  // in the Form.Item rule (fires on submit/blur) and on the backend.
  // Prior regex required `.TLD`, which rejected `foo@localhost` and
  // similar internal/intra-network addresses Auth API accepts.
  const emailLooksValid = !!emailValue && /^\S+@\S+$/.test(emailValue);
  const isAdminOrOwner = selectedRole === 'admin' || selectedRole === 'owner';
  // Use the resolver rather than `selectedSpaces.length > 0` directly —
  // a selection of just SELECT_ALL_VALUE produces zero concrete space
  // assignments when `availableSpaces` is empty. Gating on the resolver
  // prevents the submit button from enabling for a state that would
  // fail `validateSpaceAssignment` on click.
  const hasAssignedSpace = getSpaceIdsToAssign(selectedSpaces, availableSpaces).length > 0;
  const canSubmit = emailLooksValid && (isAdminOrOwner || hasAssignedSpace);

  return (
    <Modal
      title="Invite Member"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="!bg-btn-action-bg !text-btn-action-text hover:!bg-btn-action-hover !border-btn-action-bg disabled:!bg-interactive-disabled disabled:!border-interactive-disabled disabled:!text-content-on-color"
        >
          Invite
        </Button>,
      ]}
      width={520}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 12 }}
        className="mt-4"
        colon
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Please enter a valid email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
          className="mb-3"
        >
          <Input placeholder="name@company.com" />
        </Form.Item>

        <Form.Item
          label="Role"
          name="role"
          rules={[{ required: true, message: 'Please select a role' }]}
          initialValue="member"
          className="mb-3"
        >
          <Select onChange={handleRoleChange}>
            <Option value="member">Team Member</Option>
            <Option value="admin">Admin</Option>
            {isSuperUser && <Option value="owner">Owner</Option>}
          </Select>
        </Form.Item>

        <div className="mb-4 bg-surface-sunken rounded-lg p-4 mt-4" style={{ marginLeft: 0 }}>
          <div className="mb-2 text-sm font-medium text-content-primary">
            Space Assignment
            {selectedRole === 'admin' || selectedRole === 'owner' ? (
              <span className="text-content-tertiary font-normal ml-1">(optional)</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <TreeSelect
              treeData={treeData}
              value={selectedSpaces}
              onChange={handleSpaceChange}
              placeholder="Select a space"
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              treeDefaultExpandedKeys={defaultExpandedKeys}
              showSearch={false}
              className="flex-1"
              maxTagCount={3}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              status={spaceError ? 'error' : undefined}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeIcon
            />
            <Select
              value={relation}
              onChange={(value) => setRelation(value)}
              style={{ width: 120 }}
            >
              <Option value="can_view">Can View</Option>
              <Option value="can_edit">Can Edit</Option>
            </Select>
          </div>
          {spaceError && (
            <div className="text-xs mt-1" style={{ color: 'var(--color-status-error)' }}>
              {spaceError}
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default InviteUserModal;
